import { z } from "zod";

/**
 * First user message for a workflow-dispatched agent run whose node config
 * carries no prompt and whose run produced no workflow context. Mirrors the
 * scheduled-run default in @quovy/agents — the model must never receive an
 * empty opening message.
 */
export const RUN_AGENT_DEFAULT_MESSAGE =
  "This agent run was triggered automatically by a workflow.";

/**
 * Contract for the `run_agent` action node — dispatches a standalone agent
 * run (an `agent_threads` row, source/trigger_type `workflow`) with a prompt.
 * This is `create_task`'s agent dispatch without the task machinery: no task
 * row, no acknowledgment comment.
 *
 * The step has two shapes, selected by `awaitCompletion`:
 *
 * - **Fire-and-forget** (default) — the step completes as soon as the thread
 *   is queued and the run continues while the agent works. Only
 *   `threadPublicId` / `threadUrl` / `agentPublicId` are populated; every
 *   completion field is null.
 * - **Blocking** (`awaitCompletion: true`) — the step suspends on
 *   `agent_run.<stepPublicId>.completed`. The worker's thread-completion hook
 *   emits that event when the run reaches a terminal status, waking the run
 *   with the agent's final message, structured output, and emitted files.
 *
 * Unlike create_task (whose permissive config predates contracts), this node
 * is new, so the config schema enforces its one required field: an agent must
 * be selected for publish to succeed. `prompt` stays optional — the worker
 * falls back to {@link RUN_AGENT_DEFAULT_MESSAGE} so the model never receives
 * an empty user turn.
 */
export const runAgentInputSchema = z.object({}).passthrough();

const AGENT_REQUIRED_MESSAGE = "Select an agent to run";

export const runAgentConfigSchema = z
  .object({
    /** Public id of the workspace agent the thread runs. */
    agentPublicId: z.string({ error: AGENT_REQUIRED_MESSAGE }).min(1, AGENT_REQUIRED_MESSAGE),
    /**
     * First user message posted on the thread. Supports
     * `{{ steps.<node>.output.* }}` template tags, resolved at execute time.
     */
    prompt: z.string().optional(),
    /**
     * Append the run-context summary (trigger input + prior step outputs)
     * after the prompt, so an authored prompt doesn't have to hand-plumb
     * every upstream field. Default false — the agent sees only the prompt.
     */
    includeWorkflowContext: z.boolean().optional(),
    /**
     * Template-or-literal file public ids (`fil_…`) to mount for the run,
     * same resolution rules as send_email's `attachments`.
     */
    attachFileIds: z.array(z.string()).optional(),
    /**
     * Block the workflow on the run's terminal status and surface its result
     * to downstream nodes. Default false — the step completes as soon as the
     * run is queued.
     */
    awaitCompletion: z.boolean().optional(),
    /**
     * On the blocking path, whether a failed/canceled run fails the workflow
     * run (default true). When false the terminal status lands in the output
     * for a downstream condition to branch on. Ignored when
     * `awaitCompletion` is off — the step is already done by then.
     */
    failOnError: z.boolean().optional(),
  })
  .passthrough();

export const runAgentOutputSchema = z.object({
  /** Public id of the dispatched agent thread. Always present. */
  threadPublicId: z.string(),
  /**
   * Absolute URL to the thread in the web app
   * (`/w/<slug>/agent_threads/<id>`). Null when the worker has no configured
   * web origin (`CORS_ORIGIN` / `APP_URL`).
   */
  threadUrl: z.string().nullable(),
  /** Public id of the agent the thread runs. */
  agentPublicId: z.string(),
  /**
   * Terminal status on the blocking path: "completed" | "failed" |
   * "canceled". Null on the fire-and-forget path — the run hasn't finished
   * when the step completes.
   */
  status: z.string().nullable(),
  /** Final assistant message text (blocking path). */
  outcome: z.string().nullable(),
  /**
   * Structured output when the agent has a typed output contract — the
   * submit-tool payload, always an object or null (the runner rejects arrays
   * and scalars). Its keys are the agent's own, so the dataflow checker
   * accepts any path below it (`…output.structuredOutput.invoice.total`).
   */
  structuredOutput: z.record(z.string(), z.unknown()).nullable(),
  /** Public ids of files the run emitted (`output_artifacts_json`). */
  generatedFileIds: z.array(z.string()),
  /** ISO timestamp of run completion (blocking path). */
  completedAt: z.string().nullable(),
  /** Error message when the run failed and `failOnError` is off. */
  error: z.string().nullable(),
});

export type RunAgentOutput = z.infer<typeof runAgentOutputSchema>;
